/**
 * Local-only override that lets the portal sign in against `cognito-fake`
 * (mika-monorepo's local Cognito stand-in, `scripts/local/cognito-fake.mjs`)
 * instead of real AWS Cognito.
 *
 * ## Why this exists
 *
 * Amplify's default Cognito sign-in flow is `CUSTOM_WITH_SRP` (see
 * `loginSlice.ts`), which performs the real SRP (Secure Remote Password)
 * challenge-response protocol client-side. `cognito-fake` deliberately does
 * NOT implement SRP — it is a real cryptographic protocol, and a subtly
 * wrong from-scratch implementation would silently accept things real
 * Cognito rejects. `cognito-fake` only implements the simpler
 * `USER_PASSWORD_AUTH` flow (a plaintext username/password `InitiateAuth`
 * call). This module lets a local build request that flow instead, without
 * touching how a real, deployed build signs in.
 *
 * ## Off by default
 *
 * With `VITE_LOCAL_AUTH_FLOW` unset (its state in every committed
 * `.env.<mode>` file except `.env.local-dev`), {@link isLocalAuthFlowEnabled}
 * is `false` and {@link localAuthFlowType} is `undefined` — `loginSlice.ts`
 * takes the exact same `CUSTOM_WITH_SRP` path it always has.
 *
 * ## Structurally impossible to enable in a deployed build
 *
 * This selects which Cognito auth flow the browser uses to sign a user in —
 * a real authentication-surface decision, not something to fail softly on.
 * Mirrors the discipline `apps/identity-provider`'s own local-only override
 * uses (`src/config/local-cognito-jwt-override.ts`, gated on `STAGE`): this
 * throws, both at Vite config-evaluation time (`vite.config.ts`, so a
 * misconfigured build fails the BUILD itself, before any bundle is
 * produced) and again here at module-evaluation time (so a misconfigured
 * bundle that somehow got built still fails loudly the moment the app
 * boots in a browser, rather than silently degrading), if
 * `VITE_LOCAL_AUTH_FLOW` is set while the Vite `MODE` is not
 * {@link LOCAL_DEV_MODE}.
 *
 * `import.meta.env.MODE` is this app's own deployment-environment signal:
 * every real build passes an explicit `--mode` (`development` for
 * `build-dev`, `uat` for `build-uat`, `production` for `build`) baked into
 * the bundle at BUILD time — none of those is ever `'local-dev'`, and
 * `'local-dev'` is not a mode any deployed build script uses. A build
 * server can only ever produce a bundle where this override is inert.
 */

export const LOCAL_DEV_MODE = 'local-dev'

export type LocalAuthFlowType = 'USER_PASSWORD_AUTH'

const LOCAL_AUTH_FLOW_TYPE: LocalAuthFlowType = 'USER_PASSWORD_AUTH'

export interface LocalAuthFlowEnv {
  MODE?: string
  VITE_LOCAL_AUTH_FLOW?: string
}

/**
 * Pure decision function — importable and testable without Vite's
 * `import.meta.env` runtime. {@link isLocalAuthFlowEnabled} /
 * {@link localAuthFlowType} below call this with the real `import.meta.env`.
 */
export const resolveLocalAuthFlow = (
  env: LocalAuthFlowEnv,
): LocalAuthFlowType | undefined => {
  const enabled = env.VITE_LOCAL_AUTH_FLOW === 'true'

  if (!enabled) {
    if (env.MODE === LOCAL_DEV_MODE) {
      throw new Error(
        `Vite mode is ${JSON.stringify(LOCAL_DEV_MODE)} but VITE_LOCAL_AUTH_FLOW ` +
          "is not 'true' — refusing to guess whether the local Cognito auth " +
          'flow override should be active. Set VITE_LOCAL_AUTH_FLOW=true in ' +
          '.env.local-dev, or run a different mode.',
      )
    }
    return undefined
  }

  if (env.MODE !== LOCAL_DEV_MODE) {
    throw new Error(
      `VITE_LOCAL_AUTH_FLOW is set but the Vite mode is ${JSON.stringify(env.MODE ?? '')}, ` +
        `not ${JSON.stringify(LOCAL_DEV_MODE)}. This flag switches Amplify from the real ` +
        'SRP sign-in flow to USER_PASSWORD_AUTH against a fake Cognito pool and must ' +
        "never be enabled outside mika-monorepo's local stack.",
    )
  }

  return LOCAL_AUTH_FLOW_TYPE
}

/**
 * `undefined` in every real build (see module doc). Only `'USER_PASSWORD_AUTH'`
 * when `vite --mode local-dev` / `vite build --mode local-dev` is running
 * with `.env.local-dev` loaded.
 */
export const localAuthFlowType: LocalAuthFlowType | undefined =
  resolveLocalAuthFlow(import.meta.env)

export const isLocalAuthFlowEnabled = localAuthFlowType !== undefined
